import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { setCurrentOrgId } from './current-org';

/** One org the user belongs to (mirrors the worker OrgMembershipRow). */
export interface UserOrg {
  orgId: string;
  orgName: string;
  orgSlug: string;
  role: 'OrgAdmin' | 'Member';
}

interface OrgContextValue {
  orgs: UserOrg[];
  current: UserOrg | null;
  setCurrent: (org: UserOrg) => void;
  loading: boolean;
}

const OrgContext = createContext<OrgContextValue>({ orgs: [], current: null, setCurrent: () => {}, loading: true });

const STORAGE_KEY = 'nim-current-org';

export function OrgProvider({ children }: { children: React.ReactNode }) {
  const [orgs, setOrgs] = useState<UserOrg[]>([]);
  const [current, setCurrentState] = useState<UserOrg | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { orgs: list } = await api<{ orgs: UserOrg[] }>('/api/orgs');
        if (cancelled) return;
        const normalized: UserOrg[] = (list ?? []).map((o) => ({
          orgId: o.orgId,
          orgName: o.orgName,
          orgSlug: o.orgSlug,
          role: (o.role === 'OrgAdmin' ? 'OrgAdmin' : 'Member') as UserOrg['role'],
        }));
        setOrgs(normalized);
        // Resume the last selection if still a member; else default to the first org.
        const saved = typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
        const match = normalized.find((o) => o.orgId === saved);
        setCurrentState(match ?? normalized[0] ?? null);
      } catch {
        /* Worker offline / unauthorized — run with no org context. */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Keep the module-level bridge (for `api()`) + localStorage in sync.
  useEffect(() => {
    setCurrentOrgId(current?.orgId ?? null);
    if (current) localStorage.setItem(STORAGE_KEY, current.orgId);
  }, [current]);

  const value = useMemo<OrgContextValue>(() => ({
    orgs, current, loading,
    setCurrent: (org) => setCurrentState(org),
  }), [orgs, current, loading]);

  return React.createElement(OrgContext.Provider, { value }, children);
}

export function useOrg(): OrgContextValue {
  return useContext(OrgContext);
}
